import BasePointerHandler from '@/modules/three/common/curve/interactiveSpline/BasePointerHandler.js';
import { BufferGeometry, CatmullRomCurve3, Mesh, MeshBasicMaterial, TubeGeometry, Vector3 } from 'three';
import RaycasterClass from '@modules/three/common/raycast';
import { threeStore } from '@modules/three/store/threeStore';
import SplineControlPoint from './SplineControlPoint';
import { SEGMENTATION_VISUAL } from '@modules/three/common/visual/segmentationVisuals';

export default class DrawingHandler extends BasePointerHandler {
  private startIndex: number | null = null;
  private tempPositions: Vector3[] = [];
  private temporaryLine: Mesh | null = null;
  private readonly distanceThreshold: number = 0.15;
  private readonly endPointPickThreshold: number = 2.5;

  onPointerDown(selectedPoint: SplineControlPoint): void {
    const listIndex = this.interactiveSpline.indexOfControlPoint(selectedPoint);
    if (listIndex < 0) return;
    this.startIndex = listIndex;
    this.tempPositions = [];
    threeStore.getState().rootState.controls.enabled = false;
    this.temporaryLine = new Mesh(
      new BufferGeometry(),
      new MeshBasicMaterial({
        color: SEGMENTATION_VISUAL.splineDrawPreview,
        transparent: true,
        opacity: SEGMENTATION_VISUAL.splineDrawPreviewOpacity,
        depthWrite: false,
      }),
    );
    threeStore.getState().rootState.scene.add(this.temporaryLine);
    this.interactiveSpline.hideOthers();
  }

  onPointerMove(event: PointerEvent): void {
    if (this.startIndex === null) return;
    const intersection = RaycasterClass.getFirstObjectIntersection(event, this.interactiveSpline.parentMesh);
    if (!intersection) return;

    const lastPosition = this.tempPositions[this.tempPositions.length - 1];
    if (this.tempPositions.length > 0 && intersection.point.distanceTo(lastPosition) < this.distanceThreshold) {
      return;
    }
    this.tempPositions.push(intersection.point.clone());
    if (this.tempPositions.length <= 1) return;
    this.updateTemporaryLine();
  }

  onPointerUp(event: PointerEvent): void {
    this.interactiveSpline.showAll();
    this.removeTemporaryLine();
    threeStore.getState().rootState.controls.enabled = true;
    if (this.startIndex === null) {
      this.tempPositions = [];
      return;
    }

    const endIndex = this.resolveEndIndex(event);
    if (endIndex === null || this.startIndex === endIndex) {
      this.tempPositions = [];
      this.startIndex = null;
      return;
    }

    const filteredPositions = this.decimateStroke(this.tempPositions);
    const startNodePos = this.interactiveSpline.linePoints.getNodeAt(this.startIndex)?.value.position;
    const endNodePos = this.interactiveSpline.linePoints.getNodeAt(endIndex)?.value.position;

    if (startNodePos && filteredPositions.length > 0 && filteredPositions[0].distanceTo(startNodePos) < this.distanceThreshold) {
      filteredPositions.shift();
    }
    if (endNodePos && filteredPositions.length > 0 && filteredPositions[filteredPositions.length - 1].distanceTo(endNodePos) < this.distanceThreshold) {
      filteredPositions.pop();
    }

    if (filteredPositions.length === 0) {
      this.tempPositions = [];
      this.startIndex = null;
      return;
    }

    const meshObjects = filteredPositions.map((pos) => this.interactiveSpline.createSplineControlPoint(pos, 0));
    const useForwardArc = this.shouldReplaceForwardArc(this.startIndex, endIndex, filteredPositions);

    if (useForwardArc) {
      this.interactiveSpline.linePoints.replaceBetween(this.startIndex, endIndex, meshObjects);
    } else {
      const reversed = meshObjects.toReversed();
      this.interactiveSpline.linePoints.replaceBetween(endIndex, this.startIndex, reversed);
    }

    this.startIndex = null;
    this.tempPositions = [];
    this.interactiveSpline.reindexControlPoints();
    this.interactiveSpline.updateLine();
  }

  /**
   * Picks the end control point from a raycast hit, or the closest point to the release location.
   */
  private resolveEndIndex(event: PointerEvent): number | null {
    const controlPoints = this.interactiveSpline.linePoints.toArray();
    const hit = RaycasterClass.getFirstObjectsIntersection(event, controlPoints);
    if (hit) {
      return hit.index;
    }

    const meshHit = RaycasterClass.getFirstObjectIntersection(event, this.interactiveSpline.parentMesh);
    if (!meshHit) {
      return null;
    }

    let closestIndex: number | null = null;
    let closestDistance = this.endPointPickThreshold;
    this.interactiveSpline.linePoints.forEach((point, index) => {
      const distance = point.position.distanceTo(meshHit.point);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  /**
   * Chooses the arc that best matches the drawn stroke instead of always taking the shorter one.
   */
  private shouldReplaceForwardArc(startIndex: number, endIndex: number, stroke: Vector3[]): boolean {
    const totalSize = this.interactiveSpline.linePoints.size;
    const forwardDistance = (endIndex - startIndex + totalSize) % totalSize;
    const backwardDistance = (startIndex - endIndex + totalSize) % totalSize;

    if (forwardDistance === backwardDistance) {
      const forwardCost = this.measureStrokeToArc(stroke, this.collectArcPositions(startIndex, endIndex));
      const backwardCost = this.measureStrokeToArc(stroke, this.collectArcPositions(endIndex, startIndex));
      return forwardCost <= backwardCost;
    }

    const forwardCost = this.measureStrokeToArc(stroke, this.collectArcPositions(startIndex, endIndex));
    const backwardCost = this.measureStrokeToArc(stroke, this.collectArcPositions(endIndex, startIndex));
    const minCost = Math.min(forwardCost, backwardCost);
    const maxCost = Math.max(forwardCost, backwardCost);

    // Prefer the arc the stroke follows; fall back to shorter arc when ambiguous.
    if (maxCost <= 0 || minCost / maxCost <= 0.72) {
      return forwardCost <= backwardCost;
    }
    return forwardDistance <= backwardDistance;
  }

  private collectArcPositions(fromIndex: number, toIndex: number): Vector3[] {
    const positions: Vector3[] = [];
    const totalSize = this.interactiveSpline.linePoints.size;
    if (totalSize === 0 || fromIndex < 0 || fromIndex >= totalSize || toIndex < 0 || toIndex >= totalSize) {
      return positions;
    }

    let index = fromIndex;
    let node = this.interactiveSpline.linePoints.getNodeAt(index);
    if (!node) return positions;

    positions.push(node.value.position.clone());
    while (index !== toIndex) {
      index = (index + 1) % totalSize;
      node = this.interactiveSpline.linePoints.getNodeAt(index);
      if (!node) break;
      positions.push(node.value.position.clone());
    }
    return positions;
  }

  private measureStrokeToArc(stroke: Vector3[], arc: Vector3[]): number {
    if (stroke.length === 0 || arc.length < 2) {
      return Number.POSITIVE_INFINITY;
    }

    let total = 0;
    for (const point of stroke) {
      let minDistance = Number.POSITIVE_INFINITY;
      for (let i = 0; i < arc.length - 1; i++) {
        minDistance = Math.min(minDistance, this.distancePointToSegment(point, arc[i], arc[i + 1]));
      }
      total += minDistance;
    }
    return total / stroke.length;
  }

  private distancePointToSegment(point: Vector3, a: Vector3, b: Vector3): number {
    const ab = b.clone().sub(a);
    const lengthSq = ab.lengthSq();
    if (lengthSq === 0) {
      return point.distanceTo(a);
    }
    const t = Math.max(0, Math.min(1, point.clone().sub(a).dot(ab) / lengthSq));
    const projection = a.clone().add(ab.multiplyScalar(t));
    return point.distanceTo(projection);
  }

  private decimateStroke(positions: Vector3[]): Vector3[] {
    if (positions.length <= 2) {
      return positions.map((p) => p.clone());
    }
    return positions.filter((_, index) => index % 2 === 0).map((p) => p.clone());
  }

  private updateTemporaryLine(): void {
    if (!this.temporaryLine) return;
    this.temporaryLine.geometry.dispose();
    if (this.tempPositions.length === 0) return;
    const positions = this.tempPositions.map((p) => p.clone());
    const curve = new CatmullRomCurve3(positions, false);
    this.temporaryLine.geometry = new TubeGeometry(curve, 24, SEGMENTATION_VISUAL.splineTubeRadius * 1.05, 6, false);
  }

  private removeTemporaryLine(): void {
    if (!this.temporaryLine) return;
    threeStore.getState().rootState.scene.remove(this.temporaryLine);
    this.temporaryLine.geometry.dispose();
    if (this.temporaryLine.material instanceof MeshBasicMaterial) {
      this.temporaryLine.material.dispose();
    }
    this.temporaryLine = null;
  }
}
